/* =========================================================================
   Barra de destaques - Slider Web Component
   Section: barra-destaques.liquid
   ========================================================================= */

if (!customElements.get('barra-destaques-slider')) {
  class BarraDestaquesSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.isPaused = false;
      this.slides = [];
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.barra-destaques__slide'));
      this.totalSlides = this.slides.length;
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;

      if (this.totalSlides <= 1) {
        return;
      }

      this.prevBtn = this.querySelector('.barra-destaques__arrow--prev');
      this.nextBtn = this.querySelector('.barra-destaques__arrow--next');

      this.bindEvents();
      this.updateHeight();

      if (this.autoplay) {
        this.startAutoplay();
      }

      // Recalcula altura em resize
      this.resizeObserver = new ResizeObserver(() => this.updateHeight());
      this.resizeObserver.observe(this);

      // Pausa auto-rotação em modo de edição do tema
      if (window.Shopify && window.Shopify.designMode) {
        this.handleDesignMode();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.goTo(this.currentIndex - 1);
          this.restartAutoplay();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.goTo(this.currentIndex + 1);
          this.restartAutoplay();
        });
      }

      // Pausa autoplay no hover
      this.addEventListener('mouseenter', () => {
        this.isPaused = true;
      });

      this.addEventListener('mouseleave', () => {
        this.isPaused = false;
      });

      // Pausa autoplay quando foco dentro da seção
      this.addEventListener('focusin', () => {
        this.isPaused = true;
      });

      this.addEventListener('focusout', () => {
        this.isPaused = false;
      });

      // Pausa autoplay quando aba não está visível
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.isPaused = true;
        } else {
          this.isPaused = false;
        }
      });

      // Swipe/touch support (mobile)
      this.addTouchSupport();

      // Teclado (acessibilidade)
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.goTo(this.currentIndex - 1);
          this.restartAutoplay();
        } else if (e.key === 'ArrowRight') {
          this.goTo(this.currentIndex + 1);
          this.restartAutoplay();
        }
      });
    }

    addTouchSupport() {
      let touchStartX = 0;
      let touchEndX = 0;
      const threshold = 40;

      const viewport = this.querySelector('.barra-destaques__viewport');
      if (!viewport) return;

      viewport.addEventListener(
        'touchstart',
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      viewport.addEventListener(
        'touchend',
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          const diff = touchStartX - touchEndX;

          if (Math.abs(diff) > threshold) {
            if (diff > 0) {
              this.goTo(this.currentIndex + 1);
            } else {
              this.goTo(this.currentIndex - 1);
            }
            this.restartAutoplay();
          }
        },
        { passive: true }
      );
    }

    goTo(index) {
      // Wrap around (loop infinito)
      if (index < 0) {
        index = this.totalSlides - 1;
      } else if (index >= this.totalSlides) {
        index = 0;
      }

      this.slides.forEach((slide, i) => {
        if (i === index) {
          slide.classList.add('is-active');
          slide.setAttribute('aria-hidden', 'false');
        } else {
          slide.classList.remove('is-active');
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      this.currentIndex = index;
      this.updateHeight();
    }

    updateHeight() {
      // Garante que o viewport tenha altura suficiente para o maior slide
      const viewport = this.querySelector('.barra-destaques__viewport');
      const track = this.querySelector('.barra-destaques__track');
      if (!viewport || !track) return;

      let maxHeight = 0;
      this.slides.forEach((slide) => {
        const prevVisibility = slide.style.visibility;
        const prevOpacity = slide.style.opacity;
        const prevPosition = slide.style.position;

        slide.style.visibility = 'hidden';
        slide.style.opacity = '1';
        slide.style.position = 'static';

        const h = slide.offsetHeight;
        if (h > maxHeight) maxHeight = h;

        slide.style.visibility = prevVisibility;
        slide.style.opacity = prevOpacity;
        slide.style.position = prevPosition;
      });

      if (maxHeight > 0) {
        track.style.minHeight = maxHeight + 'px';
      }

      // Reaplica estado ativo
      this.slides.forEach((slide, i) => {
        if (i === this.currentIndex) {
          slide.classList.add('is-active');
        }
      });
    }

    startAutoplay() {
      if (this.totalSlides <= 1) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        if (!this.isPaused) {
          this.goTo(this.currentIndex + 1);
        }
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      if (this.autoplay) {
        this.startAutoplay();
      }
    }

    handleDesignMode() {
      // Quando o lojista seleciona um bloco no editor do tema, mostra aquele slide
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        const selectedSlide = event.target.closest('.barra-destaques__slide');
        if (!selectedSlide) return;

        const index = parseInt(selectedSlide.dataset.index, 10);
        if (!isNaN(index)) {
          this.goTo(index);
          this.stopAutoplay();
        }
      });

      document.addEventListener('shopify:block:deselect', (event) => {
        if (!this.contains(event.target)) return;
        this.restartAutoplay();
      });

      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.contains(this)) {
          this.updateHeight();
        }
      });
    }
  }

  customElements.define('barra-destaques-slider', BarraDestaquesSlider);
}
